import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';

import Modal from 'components/ui/Modal';
import { ResponsiveColumn, Row, Column } from 'components/shared/Layout';
import { isValidName } from 'utils/validators';

import { SaveButton, ExitButton } from '../../Passengers.styles';
import {
  ButtonRow,
  ErrorMessage,
  FieldControl,
  FieldLabel,
  FieldWrapper,
  IntroText,
  RequiredMark,
  StyledInput,
  StyledSelect
} from './AddInfantForm.styles';

const formatPassengerLabel = ({ firstName, lastName, passengerId }) => {
  const passengerNumber = passengerId?.split('_')?.[1] || passengerId;

  return `${passengerNumber} - ${firstName} ${lastName}`;
};

const getDefaultFormValues = (availableInfantPassengers) => ({
  firstName: '',
  lastName: '',
  passengerId: availableInfantPassengers[0]?.passengerId || ''
});

const AddInfantForm = ({ availableInfantPassengers, isOpen, onClose, onSave }) => {
  const [isSaving, setIsSaving] = useState(false);
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: getDefaultFormValues(availableInfantPassengers)
  });

  useEffect(() => {
    reset(getDefaultFormValues(availableInfantPassengers));
  }, [availableInfantPassengers, reset, isOpen]);

  const handleAddInfant = async (formData) => {
    setIsSaving(true);

    try {
      await onSave(formData);
      onClose();
    } catch {
      return;
    } finally {
      setIsSaving(false);
    }
  };

  const isSaveDisabled = availableInfantPassengers.length === 0 || isSaving;

  return (
    <Modal isOpen={isOpen} minWidth="720px" onClose={onClose} title={<FormattedMessage id="buttons.addInfantWithoutSeat" />}>
      <form data-testid="add-infant-form" onSubmit={handleSubmit(handleAddInfant)}>
        <Row columns={16} marginBottom="0">
          <Column span={16}>
            <IntroText>
              <FormattedMessage id="passengerDetails.addInfantDescription" />
            </IntroText>
          </Column>
          <ResponsiveColumn bigScreens={6} smallScreens={16}>
            <FieldWrapper>
              <FieldLabel htmlFor="passengerId">
                <FormattedMessage id="passengerDetails.infantGuardian" />
                <RequiredMark>*</RequiredMark>
              </FieldLabel>
              <FieldControl>
                <StyledSelect
                  data-cy="infant-passenger-select"
                  data-testid="infant-passenger-select"
                  disabled={isSaving}
                  id="passengerId"
                  {...register('passengerId')}
                >
                  {availableInfantPassengers.map((passenger) => (
                    <option key={passenger.passengerId} value={passenger.passengerId}>
                      {formatPassengerLabel(passenger)}
                    </option>
                  ))}
                </StyledSelect>
                <ErrorMessage aria-hidden $visible={false} />
              </FieldControl>
            </FieldWrapper>
          </ResponsiveColumn>
          <ResponsiveColumn bigScreens={5} smallScreens={16}>
            <FieldWrapper>
              <FieldLabel htmlFor="firstName">
                <FormattedMessage id="passengerDetails.firstName" />
                <RequiredMark>*</RequiredMark>
              </FieldLabel>
              <FieldControl>
                <StyledInput
                  data-cy="infant-first-name"
                  data-testid="infant-first-name"
                  disabled={isSaving}
                  id="firstName"
                  type="text"
                  validationState={errors.firstName ? 'error' : ''}
                  {...register('firstName', {
                    required: 'advancedPaxInfo.invalidFirstName',
                    validate: (value) => isValidName(value) || 'paxForm.validator.invalidFirstName'
                  })}
                />
                <ErrorMessage $visible={Boolean(errors.firstName)}>
                  {errors.firstName ? <FormattedMessage id={errors.firstName.message} /> : null}
                </ErrorMessage>
              </FieldControl>
            </FieldWrapper>
          </ResponsiveColumn>
          <ResponsiveColumn bigScreens={5} smallScreens={16}>
            <FieldWrapper>
              <FieldLabel htmlFor="lastName">
                <FormattedMessage id="passengerDetails.lastName" />
                <RequiredMark>*</RequiredMark>
              </FieldLabel>
              <FieldControl>
                <StyledInput
                  data-cy="infant-last-name"
                  data-testid="infant-last-name"
                  disabled={isSaving}
                  id="lastName"
                  type="text"
                  validationState={errors.lastName ? 'error' : ''}
                  {...register('lastName', {
                    required: 'advancedPaxInfo.invalidLastName',
                    validate: (value) => isValidName(value) || 'paxForm.validator.invalidLastName'
                  })}
                />
                <ErrorMessage $visible={Boolean(errors.lastName)}>
                  {errors.lastName ? <FormattedMessage id={errors.lastName.message} /> : null}
                </ErrorMessage>
              </FieldControl>
            </FieldWrapper>
          </ResponsiveColumn>
        </Row>
        <ButtonRow>
          <ExitButton data-cy="cancel-add-infant" data-testid="cancel-add-infant" disabled={isSaving} onClick={onClose} type="button">
            <FormattedMessage id="buttons.cancel" />
          </ExitButton>
          <SaveButton
            data-cy="save-additional-passenger"
            data-testid="save-additional-passenger"
            disabled={isSaveDisabled}
            loading={isSaving}
            type="submit"
          >
            <FormattedMessage id="buttons.saveAdditionalPassenger" />
          </SaveButton>
        </ButtonRow>
      </form>
    </Modal>
  );
};

AddInfantForm.propTypes = {
  availableInfantPassengers: PropTypes.arrayOf(
    PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      passengerId: PropTypes.string.isRequired
    }).isRequired
  ).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
};

export default AddInfantForm;
